"use client";

import { memo } from "react";
import Image from "next/image";
import Link from "@/components/ui/Link";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  title?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  /** Mark as LCP image - preloads for faster rendering */
  priority?: boolean;
  imageMetadata?: ImageMetadata;
  templatetype?: FieldConfig;
};

const VerticalMediamImageNewsComponent = ({
  title = { hidden: false, value: "லோகேஷ் கனகராஜ் இயக்குவது ரஜினியின் கடைசி படமா? நடிகர் ரஜினிகாந்த் 'ஜெயிலர்' படத்தில் நடித்து முடித்துள்ளார்.", customClass: "" },
  timeAgo = { hidden: false, value: "21 min ago", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  templatetype  = { hidden: false, value: "", customClass: "" },
}: Props) => {
  const titleValue = title?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const isLiveBlog = templatetype?.value === "live-blog" || templatetype?.value === "true";

  const content = (
    <div className="flex flex-col overflow-hidden p-4 gap-4 bg-F8F8F8">
      {/* IMAGE - Fixed aspect ratio prevents CLS */}
      {!imageSrc?.hidden && (
        <div className={`relative w-full aspect-[16/9] overflow-hidden ${imageSrc?.customClass || ""}`} style={{ contain: 'layout' }}>
          <Image
            src={imageSrcValue}
            alt={titleValue}
            fill
            priority={priority}
            className="object-cover"
            style={getFocusPointStyle(imageMetadata)}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 300px"
            placeholder="blur"
            blurDataURL={BLUR_PLACEHOLDER}
          />
        </div>
      )}

      {/* CONTENT */}
      <div className="flex flex-col gap-3">
        {!title?.hidden && (
 <h3 className={`text-14-tamil-700 text-1B1B1B leading-[1.4] h-auto ${title?.customClass || ""}`}>
            {isLiveBlog && (
              <span className="inline-flex items-center gap-1.5 mr-2 align-middle">
                <span className="relative inline-flex h-4 w-4 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-40 animate-[wave_1.5s_ease-out_infinite]" />
                  <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-60 animate-[wave_1.5s_ease-out_0.3s_infinite]" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                </span>
              </span>
            )}
            {titleValue}
          </h3>
        )}

        <div className="flex items-center justify-between gap-2">
          {!timeAgo?.hidden && (
            <span className={`text-12-tamil-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
          )}
          <div className="flex gap-3">
            <MessageIcon
              id="message-icon-1"
              slug={slug}
              category={BOOKMARK_CATEGORIES.NEWS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
};

// Memoize to prevent unnecessary re-renders
export const VerticalMediamImageNews = memo(VerticalMediamImageNewsComponent);
